import React, { useMemo } from 'react'
import styles from './ProposalDetail.module.scss'
import { formatCommonAmount } from '../../../util/formatAmount'
import dayjs from 'dayjs'
import { TAX_PERCENTAGE } from '../../../util/constants'
import { truncateText } from '../../../util'
function ProposalDetail({ proposalData, isStatus = false }) {
	const subTotal = useMemo(() => {
		return proposalData?.lineItems?.reduce((acc, item) => {
			const addOns = item?.addOns ?? item?.addOnsIds ?? []
			const addOnsTotal = addOns?.reduce((addOnsAcc, addOn) => {
				return addOnsAcc + addOn.price * (addOn.quantity ?? 1)
			}, 0)
			return acc + item.price * (item.quantity ?? 1) + addOnsTotal
		}, 0)
	}, [proposalData])
	const tax = TAX_PERCENTAGE || 10
	const taxAmount = subTotal * (tax / 100) || 0
	const isRevision = proposalData.status === 'Revision'
	const isShowStatus = proposalData.status === 'Revision' || isStatus
	const total = proposalData.total ?? subTotal + taxAmount ?? 0
	return (
		<div>
			<div className={styles.CategoryName}>
				{proposalData.businessType?.split('-')?.[0]?.trim()}
				{proposalData.businessTypes?.join(
					<span className={styles.dot} />
				)}
			</div>

			{((['Rejected', 'Declined']?.includes(proposalData?.status) &&
				proposalData.reason) ||
				(isShowStatus && proposalData.revision_description)) && (
				<div
					className={styles.Reason}
					style={{
						backgroundColor: isShowStatus ? '#F5EBF8' : '#FEECEB',
					}}
				>
					<h6 style={{ color: isShowStatus ? '#9B38BD' : '#F44336' }}>
						{isShowStatus ? 'Request Revision' : 'Reject Reason'}
					</h6>
					<p>
						{isShowStatus
							? proposalData.revision_description
							: (proposalData.reason ?? '')}
					</p>
				</div>
			)}

			<div className={styles.SummaryBanner}>
				<div className={styles.SummaryColumn}>
					<span>
						{isRevision ? 'Requested' : proposalData.status} On
					</span>
					<span>
						{proposalData.updatedAt
							? dayjs(proposalData.updatedAt).format(
									'MMM DD, YYYY, hh:mm A'
								)
							: '--'}
					</span>
				</div>
				<div className={styles.SummaryColumn}>
					<span>{!isStatus ? 'Your' : ''} Budget</span>
					<span>
						{formatCommonAmount(
							proposalData.budget || proposalData.userBudget
						)}
					</span>
				</div>
				<div className={styles.SummaryColumn}>
					<span>Vendor's Bid</span>
					<span>
						{formatCommonAmount(
							proposalData.total ||
								proposalData.bidAmount ||
								proposalData.budget
						)}
					</span>
				</div>
			</div>
			{(proposalData?.lineItems || []).length > 0 && (
				<div className={styles.SubmittedBidScreen}>
					<h2 className={styles.PriceBreakdown}>Price Breakdown</h2>
					<div className={styles.SubmittedBidCard}>
						<div className={styles.BidTableContainer}>
							<div className={styles.BidTableHeader}>
								<span>Package Name</span>
								<span>Quantity</span>
								<span>Unit Price</span>
								<span>Amount</span>
							</div>
							<div className={styles.BidTableBody}>
								{(proposalData?.lineItems || [])?.map(
									(item, idx) => (
										<React.Fragment key={idx}>
											{idx > 0 && (
												<div
													className={styles.divider}
												/>
											)}
											<div className={styles.BidTableRow}>
												<div
													className={
														styles.ColumnContent
													}
												>
													<span
														className={
															styles.SingleValue
														}
													>
														{truncateText(item.name)}
													</span>
												</div>
												<div
													className={
														styles.ColumnContent
													}
												>
													<span
														className={
															styles.SingleValue
														}
													>
														{item.quantity ?? 1}
													</span>
												</div>
												<div
													className={
														styles.ColumnContent
													}
												>
													<span
														className={
															styles.SingleValue
														}
													>
														{formatCommonAmount(
															item.price
														)}
													</span>
												</div>
												<div
													className={
														styles.ColumnContent
													}
												>
													<span
														className={
															styles.SingleValue
														}
													>
														{formatCommonAmount(
															item.price *
																(item.quantity ??
																	1)
														)}
													</span>
												</div>
											</div>
											{(
												item.addOns ||
												item.addOnsIds ||
												[]
											)?.map((addOn, aIdx) => (
												<div
													key={aIdx}
													className={`${styles.BidTableRow} ${styles.addOnRow}`}
												>
													<div
														className={`${styles.ColumnContent} ${
															aIdx ===
															(
																item.addOns ||
																item.addOnsIds ||
																[]
															).length -
																1
																? styles.lastAddOn
																: ''
														}`}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{truncateText(
																addOn.name
															)}
														</span>
													</div>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{addOn?.quantity ??
																1}
														</span>
													</div>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{formatCommonAmount(
																addOn.price ??
																	addOn.unitPrice
															)}
														</span>
													</div>
													<div
														className={
															styles.ColumnContent
														}
													>
														<span
															className={
																styles.SingleValue
															}
														>
															{formatCommonAmount(
																(addOn.price ??
																	addOn.unitPrice) *
																	(addOn.quantity ??
																		1)
															)}
														</span>
													</div>
												</div>
											))}
										</React.Fragment>
									)
								)}
							</div>
							<div className={styles.BidTableFooter}>
								<div className={styles.FooterItem}>
									<span>Subtotal:</span>
									<span>{formatCommonAmount(subTotal)}</span>
								</div>
								<div className={styles.FooterItem}>
									<span>Tax({tax}%):</span>
									<span>{formatCommonAmount(taxAmount)}</span>
								</div>
								<div
									className={`${styles.FooterItem} ${styles.total}`}
								>
									<span>Total Bid Amount:</span>
									<span>{formatCommonAmount(total)}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			<div className={styles.SubmittedBidScreen}>
				<div className={styles.SubmittedBidCard}>
					<div className={styles.SectionBox}>
						<span className={styles.SectionLabel}>Notes</span>
						<span className={styles.SectionContent}>
							{proposalData.note || 'No notes provided.'}
						</span>
					</div>

					{proposalData.attachment && (
						<div className={styles.SectionBox}>
							<span className={styles.SectionLabel}>
								Attachment
							</span>
							<div className={styles.SectionContent}>
								<div className={styles.AttachmentCard}>
									<img src="/images/document.svg" alt="doc" />
									<div className={styles.AttachmentInfo}>
										<span>
											{proposalData.attachmentName ||
												'Package Details.pdf'}
										</span>
										<img
											src="/images/task-detail-download.svg"
											alt="more"
											className={styles.options}
											onClick={() => {
												window.open(
													proposalData.attachment,
													'_blank'
												)
											}}
										/>
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	)
}

export default ProposalDetail
